import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const AUTO_ADVANCE_MS = 3000;

/** Editor de crop tipo Facebook: se arrastra dentro de un cuadrado + zoom con slider */
const CropEditor = ({ open, imageUrl, initial, onSave, onCancel }) => {
  const [posX, setPosX] = useState(initial?.object_position_x ?? 50);
  const [posY, setPosY] = useState(initial?.object_position_y ?? 50);
  const [zoom, setZoom] = useState(initial?.zoom ?? 100);
  const containerRef = useRef(null);
  const dragging = useRef(null);

  useEffect(() => {
    setPosX(initial?.object_position_x ?? 50);
    setPosY(initial?.object_position_y ?? 50);
    setZoom(initial?.zoom ?? 100);
  }, [initial, open]);

  if (!open) return null;

  const onDragStart = (clientX, clientY) => {
    dragging.current = { startX: clientX, startY: clientY, startPosX: posX, startPosY: posY };
  };
  const onDragMove = (clientX, clientY) => {
    if (!dragging.current || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const dxPct = ((clientX - dragging.current.startX) / rect.width) * 100;
    const dyPct = ((clientY - dragging.current.startY) / rect.height) * 100;
    // Al arrastrar la imagen hacia la derecha, la vista se corre a la izquierda → object-position disminuye
    setPosX(Math.max(0, Math.min(100, dragging.current.startPosX - dxPct)));
    setPosY(Math.max(0, Math.min(100, dragging.current.startPosY - dyPct)));
  };
  const onDragEnd = () => { dragging.current = null; };

  return (
    <div style={{position: 'fixed', inset: 0, background: 'rgba(0,0,0,.75)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20}} data-testid="pub-crop-editor">
      <div style={{background: 'white', borderRadius: 12, padding: 20, maxWidth: 460, width: '100%'}}>
        <h3 style={{margin: '0 0 10px', color: '#0055A4'}}>Ajustar imagen (formato cuadrado)</h3>
        <p style={{margin: '0 0 12px', fontSize: '.85em', color: '#666'}}>Arrastrá para reposicionar. Ajustá el zoom con el slider.</p>
        <div
          ref={containerRef}
          onMouseDown={e => onDragStart(e.clientX, e.clientY)}
          onMouseMove={e => onDragMove(e.clientX, e.clientY)}
          onMouseUp={onDragEnd}
          onMouseLeave={onDragEnd}
          onTouchStart={e => onDragStart(e.touches[0].clientX, e.touches[0].clientY)}
          onTouchMove={e => onDragMove(e.touches[0].clientX, e.touches[0].clientY)}
          onTouchEnd={onDragEnd}
          style={{width: '100%', aspectRatio: '1 / 1', overflow: 'hidden', borderRadius: 8, cursor: dragging.current ? 'grabbing' : 'grab', background: '#eee', userSelect: 'none'}}
        >
          <img src={imageUrl} alt="preview" draggable={false}
               style={{width: `${zoom}%`, height: `${zoom}%`, objectFit: 'cover', objectPosition: `${posX}% ${posY}%`, pointerEvents: 'none'}} />
        </div>
        <label style={{display: 'block', marginTop: 12, fontSize: '.9em', fontWeight: 'bold'}}>Zoom: {Math.round(zoom)}%</label>
        <input type="range" min="100" max="300" step="1" value={zoom} onChange={e => setZoom(parseFloat(e.target.value))}
               data-testid="pub-crop-zoom" style={{width: '100%'}} />
        <div style={{display: 'flex', gap: 8, marginTop: 15, justifyContent: 'flex-end'}}>
          <button onClick={onCancel} data-testid="pub-crop-cancel" style={{padding: '8px 14px', background: '#ccc', border: 'none', borderRadius: 5, cursor: 'pointer'}}>Cancelar</button>
          <button onClick={() => onSave({object_position_x: posX, object_position_y: posY, zoom})} data-testid="pub-crop-save"
                  style={{padding: '8px 14px', background: '#28a745', color: 'white', border: 'none', borderRadius: 5, cursor: 'pointer', fontWeight: 'bold'}}>💾 Guardar</button>
        </div>
      </div>
    </div>
  );
};

const PublicidadesCarrusel = ({ isAdmin = false, adminPassword = '' }) => {
  const [publicidades, setPublicidades] = useState([]);
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorTarget, setEditorTarget] = useState(null); // { pub | null (nuevo) }
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingUrl, setPendingUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/publicidades`);
      setPublicidades(r.data || []);
    } catch (err) { console.error('publicidades:', err); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-advance
  useEffect(() => {
    if (paused || publicidades.length < 2) return;
    const t = setInterval(() => {
      setCurrent(c => (c + 1) % publicidades.length);
    }, AUTO_ADVANCE_MS);
    return () => clearInterval(t);
  }, [paused, publicidades.length]);

  // Reset current if list shrinks
  useEffect(() => {
    if (current >= publicidades.length && publicidades.length > 0) {
      setCurrent(0);
    }
  }, [publicidades.length, current]);

  const prev = () => setCurrent(c => (c - 1 + publicidades.length) % publicidades.length);
  const next = () => setCurrent(c => (c + 1) % publicidades.length);

  const onFileChosen = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/^image\//i.test(file.type)) return alert('Elegí una imagen (JPG/PNG/WEBP).');
    if (file.size > 8 * 1024 * 1024) return alert('La imagen supera 8 MB. Comprimila antes.');
    const url = URL.createObjectURL(file);
    setPendingFile(file);
    setPendingUrl(url);
    setEditorTarget(null);
    setEditorOpen(true);
  };

  const saveNew = async ({ object_position_x, object_position_y, zoom }) => {
    if (!pendingFile) return;
    const fd = new FormData();
    fd.append('file', pendingFile);
    fd.append('admin_password', adminPassword || '');
    fd.append('object_position_x', String(object_position_x));
    fd.append('object_position_y', String(object_position_y));
    fd.append('zoom', String(zoom));
    try {
      setUploading(true);
      await axios.post(`${API}/publicidades`, fd);
      URL.revokeObjectURL(pendingUrl);
      setPendingFile(null); setPendingUrl(null);
      setEditorOpen(false);
      await load();
    } catch (err) {
      alert('❌ ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
    }
  };

  const saveEdit = async (crop) => {
    if (!editorTarget) return;
    try {
      await axios.put(`${API}/publicidades/${editorTarget.id}?admin_password=${encodeURIComponent(adminPassword || '')}`, crop);
      setEditorOpen(false); setEditorTarget(null);
      await load();
    } catch (err) {
      alert('❌ ' + (err.response?.data?.detail || err.message));
    }
  };

  const del = async (id) => {
    if (!window.confirm('¿Eliminar esta publicidad?')) return;
    try {
      await axios.delete(`${API}/publicidades/${id}?admin_password=${encodeURIComponent(adminPassword || '')}`);
      await load();
    } catch (err) {
      alert('❌ ' + (err.response?.data?.detail || err.message));
    }
  };

  const active = publicidades[current];
  const editorInitial = editorTarget || { object_position_x: 50, object_position_y: 50, zoom: 100 };
  const editorImageUrl = editorTarget ? `${BACKEND_URL}${editorTarget.url}` : pendingUrl;

  return (
    <div style={{maxWidth: 500, margin: '30px auto', padding: '0 15px'}} data-testid="publicidades-carrusel">
      <h3 style={{textAlign: 'center', color: '#0055A4', marginBottom: 10, fontSize: '1.3em'}}>📢 AUSPICIANTES</h3>

      {publicidades.length === 0 && !isAdmin && (
        <p style={{textAlign: 'center', color: '#999', fontSize: '.9em'}}>Sin publicidades cargadas aún.</p>
      )}

      {publicidades.length > 0 && active && (
        <div
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onTouchStart={() => setPaused(true)}
          onTouchEnd={() => setTimeout(() => setPaused(false), 4000)}
          style={{position: 'relative', width: '100%', aspectRatio: '1 / 1', borderRadius: 12, overflow: 'hidden', boxShadow: '0 6px 18px rgba(0,0,0,.15)', background: '#f0f0f0'}}
        >
          <img
            src={`${BACKEND_URL}${active.url}`}
            alt="publicidad"
            data-testid={`pub-slide-${current}`}
            style={{
              width: `${active.zoom || 100}%`,
              height: `${active.zoom || 100}%`,
              objectFit: 'cover',
              objectPosition: `${active.object_position_x ?? 50}% ${active.object_position_y ?? 50}%`,
              display: 'block',
              transition: 'opacity .4s',
            }}
          />

          {publicidades.length > 1 && (
            <>
              <button onClick={prev} data-testid="pub-prev-btn"
                style={{position: 'absolute', top: '50%', left: 8, transform: 'translateY(-50%)', width: 36, height: 36, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,.55)', color: 'white', cursor: 'pointer', fontSize: '1.2em'}}>‹</button>
              <button onClick={next} data-testid="pub-next-btn"
                style={{position: 'absolute', top: '50%', right: 8, transform: 'translateY(-50%)', width: 36, height: 36, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,.55)', color: 'white', cursor: 'pointer', fontSize: '1.2em'}}>›</button>
            </>
          )}

          {isAdmin && (
            <div style={{position: 'absolute', top: 8, right: 8, display: 'flex', gap: 6}}>
              <button onClick={() => { setEditorTarget(active); setEditorOpen(true); }} data-testid="pub-edit-crop-btn" title="Ajustar recorte"
                style={{width: 34, height: 34, borderRadius: '50%', border: '2px solid white', background: 'rgba(0,85,164,.9)', color: 'white', cursor: 'pointer', fontSize: '.9em'}}>✏️</button>
              <button onClick={() => del(active.id)} data-testid="pub-delete-btn" title="Eliminar"
                style={{width: 34, height: 34, borderRadius: '50%', border: '2px solid white', background: 'rgba(220,53,69,.92)', color: 'white', cursor: 'pointer', fontSize: '.9em'}}>🗑</button>
            </div>
          )}
        </div>
      )}

      {publicidades.length > 1 && (
        <div style={{display: 'flex', gap: 6, justifyContent: 'center', marginTop: 10, flexWrap: 'wrap'}}>
          {publicidades.map((_, i) => (
            <button key={i} onClick={() => setCurrent(i)} data-testid={`pub-dot-${i}`}
              style={{width: i === current ? 14 : 10, height: i === current ? 14 : 10, borderRadius: '50%', border: 'none',
                      background: i === current ? '#0055A4' : '#ccc', cursor: 'pointer', padding: 0, transition: 'all .2s'}} />
          ))}
        </div>
      )}

      {isAdmin && (
        <div style={{marginTop: 12, textAlign: 'center'}}>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || publicidades.length >= 30}
            data-testid="pub-add-btn"
            style={{padding: '10px 20px', background: publicidades.length >= 30 ? '#999' : '#28a745', color: 'white', border: 'none', borderRadius: 8, cursor: publicidades.length >= 30 ? 'not-allowed' : 'pointer', fontWeight: 'bold', fontSize: '1em'}}
          >
            {uploading ? '⏳ Subiendo...' : `➕ Agregar publicidad (${publicidades.length}/30)`}
          </button>
          <input ref={fileInputRef} type="file" accept="image/*" onChange={onFileChosen} style={{display: 'none'}} data-testid="pub-file-input" />
        </div>
      )}

      <CropEditor
        open={editorOpen}
        imageUrl={editorImageUrl}
        initial={editorInitial}
        onSave={editorTarget ? saveEdit : saveNew}
        onCancel={() => {
          if (pendingUrl) URL.revokeObjectURL(pendingUrl);
          setPendingFile(null); setPendingUrl(null);
          setEditorTarget(null);
          setEditorOpen(false);
        }}
      />
    </div>
  );
};

export default PublicidadesCarrusel;
